import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState } from "react";
import { ZoomIn } from "lucide-react";

interface SuggestionContentProps {
  content: any;
  screenshots?: Array<{ name: string; data: string }>;
}

export default function SuggestionContent({ content, screenshots = [] }: SuggestionContentProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: true,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
    ],
    content,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none',
      },
    },
  });

  if (!editor) {
    return null;
  }

  return (
    <div>
      <EditorContent editor={editor} />

      {screenshots.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            Screenshots ({screenshots.length})
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {screenshots.map((screenshot, index) => (
              <div
                key={index}
                className="relative group rounded-lg overflow-hidden border cursor-pointer"
                onClick={() => setSelectedImage(screenshot.data)}
                data-testid={`screenshot-preview-${index}`}
              >
                <img
                  src={screenshot.data}
                  alt={screenshot.name}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
                  {screenshot.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Screenshot preview"
              className="w-full h-auto"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
